import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import DashboardLayout from '../Layout/DashboardLayout';
import LoadingSpinner from '../Common/LoadingSpinner';
import { requestHelpers } from '../../services/api';
import { BRAND_LOGO } from '../../utils/brandAssets';
import './Reports.css';

const Reports = () => {
    const [loading, setLoading] = useState(true);
    const [period, setPeriod] = useState('30days');
    const [data, setData] = useState(null);
    const [generating, setGenerating] = useState(false);

    useEffect(() => {
        fetchReportData();
    }, [period]);

    const fetchReportData = async () => {
        try {
            setLoading(true);
            const response = await requestHelpers.get(`/analytics/report-summary`, {
                params: { period }
            });
            setData(response.data);
        } catch (error) {
            toast.error('Failed to fetch report data');
        } finally {
            setLoading(false);
        }
    };

    const downloadPDF = async () => {
        setGenerating(true);
        try {
            // Dynamically load jsPDF and AutoTable if not present
            if (!window.jspdf) {
                await new Promise((resolve) => {
                    const script = document.createElement('script');
                    script.src = 'https://cdnjs.cloudflare.com/ajax/libs/jspdf/2.5.1/jspdf.umd.min.js';
                    script.onload = resolve;
                    document.head.appendChild(script);
                });
            }
            if (!window.jspdf.jsPDF.API.autoTable) {
                await new Promise((resolve) => {
                    const script = document.createElement('script');
                    script.src = 'https://cdnjs.cloudflare.com/ajax/libs/jspdf-autotable/3.5.25/jspdf.plugin.autotable.min.js';
                    script.onload = resolve;
                    document.head.appendChild(script);
                });
            }

            const { jsPDF } = window.jspdf;
            const doc = new jsPDF();
            
            // 1. Header & Logo
            doc.setFillColor(59, 130, 246); // Primary blue
            doc.rect(0, 0, 210, 40, 'F');
            
            // Add Logo Image
            try {
                doc.addImage(BRAND_LOGO, 'JPEG', 15, 5, 30, 30);
            } catch (imgError) {
                console.error("Failed to add logo:", imgError);
                doc.setTextColor(255, 255, 255);
                doc.setFontSize(24);
                doc.text('MessagePe', 15, 25);
            }
            
            doc.setTextColor(255, 255, 255);
            doc.setFontSize(10);
            doc.setFont('helvetica', 'normal');
            doc.text('PERFORMANCE INSIGHTS REPORT', 50, 25);
            
            const rangeText = period === '30days' ? 'Last 30 Days' : period === '6months' ? 'Last 6 Months' : 'Last 1 Year';
            doc.text(`Generated on: ${new Date().toLocaleDateString()}`, 150, 20);
            doc.text(`Period: ${rangeText}`, 150, 26);

            // 2. Summary Section
            doc.setTextColor(0, 0, 0);
            doc.setFontSize(16);
            doc.text('Executive Summary', 15, 55);
            
            const summaryTable = [
                ['Metric', 'Value'],
                ['Total Outbound Messages', data.summary.totalSent.toLocaleString()],
                ['Average Delivery Rate', `${((data.summary.delivered / data.summary.totalSent) * 100 || 0).toFixed(1)}%`],
                ['Average Read Rate', `${((data.summary.read / data.summary.delivered) * 100 || 0).toFixed(1)}%`],
                ['Total Incoming Messages', data.summary.totalReceived.toLocaleString()],
                ['Total Investment (inc. GST)', `INR ${data.spendBreakdown.reduce((acc, curr) => acc + curr.total, 0).toLocaleString()}`]
            ];

            doc.autoTable({
                startY: 60,
                head: [summaryTable[0]],
                body: summaryTable.slice(1),
                theme: 'striped',
                headStyles: { fillColor: [59, 130, 246] }
            });

            // 3. Spend Breakdown
            doc.text('Investment Breakdown', 15, doc.lastAutoTable.finalY + 15);
            const spendTable = [
                ['Category', 'Volume', 'Total Amount'],
                ...data.spendBreakdown.map(s => [
                    s._id.charAt(0).toUpperCase() + s._id.slice(1),
                    s.count.toLocaleString(),
                    `INR ${s.total.toLocaleString()}`
                ])
            ];

            doc.autoTable({
                startY: doc.lastAutoTable.finalY + 20,
                head: [spendTable[0]],
                body: spendTable.slice(1),
                theme: 'grid'
            });

            // 4. Monthly Performance (if applicable)
            if (data.monthlyBreakdown.length > 0) {
                doc.addPage();
                doc.text('Monthly Trends', 15, 20);
                const monthNames = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
                const monthlyTable = [
                    ['Month', 'Messages Processed', 'Monthly Spend'],
                    ...data.monthlyBreakdown.map(m => [
                        `${monthNames[m._id.month - 1]} ${m._id.year}`,
                        m.messages.toLocaleString(),
                        `INR ${m.spend.toLocaleString()}`
                    ])
                ];

                doc.autoTable({
                    startY: 25,
                    head: [monthlyTable[0]],
                    body: monthlyTable.slice(1),
                    theme: 'striped'
                });
            }

            // 5. Top Campaigns
            doc.text('Top Campaigns Performance', 15, doc.lastAutoTable.finalY + 15);
            const campTable = [
                ['Campaign Name', 'Sent', 'Read %', 'Status'],
                ...data.topCampaigns.map(c => [
                    c.name,
                    c.sent.toLocaleString(),
                    `${((c.read / c.delivered) * 100 || 0).toFixed(1)}%`,
                    c.status.toUpperCase()
                ])
            ];

            doc.autoTable({
                startY: doc.lastAutoTable.finalY + 20,
                head: [campTable[0]],
                body: campTable.slice(1),
                theme: 'grid'
            });

            // Footer
            const pageCount = doc.internal.getNumberOfPages();
            for (let i = 1; i <= pageCount; i++) {
                doc.setPage(i);
                doc.setFontSize(8);
                doc.setTextColor(150, 150, 150);
                doc.text(`Official MessagePe Report | Confidential | Page ${i} of ${pageCount}`, 105, 285, { align: 'center' });
            }

            doc.save(`MessagePe_Report_${period}_${new Date().toISOString().slice(0,10)}.pdf`);
            toast.success('Report downloaded successfully!');
        } catch (error) {
            console.error('PDF Error:', error);
            toast.error('Failed to generate PDF');
        } finally {
            setGenerating(false);
        }
    };

    return (
        <DashboardLayout>
            <div className="reports-page">
                <div className="page-header">
                    <div className="header-content">
                        <h1>Reports & Insights</h1>
                        <p>Generate and download detailed performance summaries</p>
                    </div>
                    <div className="header-actions">
                        <select 
                            value={period} 
                            onChange={(e) => setPeriod(e.target.value)}
                            className="period-select"
                        >
                            <option value="30days">Last 30 Days</option>
                            <option value="6months">Last 6 Months</option>
                            <option value="1year">Last 1 Year</option>
                        </select>
                        <button 
                            className="btn btn--primary" 
                            onClick={downloadPDF}
                            disabled={loading || generating || !data}
                        >
                            {generating ? <i className="fas fa-spinner fa-spin mr-2"></i> : <i className="fas fa-download mr-2"></i>}
                            Download PDF Report
                        </button>
                    </div>
                </div>

                {loading ? (
                    <div className="loading-state"><LoadingSpinner /></div>
                ) : (
                    <div className="reports-content animate-fade-in">
                        <div className="stats-grid">
                            <div className="stat-card">
                                <span className="label">Total Sent</span>
                                <span className="value">{data.summary.totalSent.toLocaleString()}</span>
                                <span className="sub">Messages</span>
                            </div>
                            <div className="stat-card">
                                <span className="label">Read Rate</span>
                                <span className="value">
                                    {((data.summary.read / data.summary.delivered) * 100 || 0).toFixed(1)}%
                                </span>
                                <span className="sub">Average</span>
                            </div>
                            <div className="stat-card">
                                <span className="label">Total Spend</span>
                                <span className="value">
                                    ₹{data.spendBreakdown.reduce((acc, curr) => acc + curr.total, 0).toLocaleString()}
                                </span>
                                <span className="sub">Net Investment</span>
                            </div>
                        </div>

                        <div className="report-sections">
                            <div className="report-card">
                                <h3>Category-wise Investment</h3>
                                <div className="spend-list">
                                    {data.spendBreakdown.map(s => (
                                        <div key={s._id} className="spend-item">
                                            <div className="item-info">
                                                <span className="cat-name">{s._id.toUpperCase()}</span>
                                                <span className="cat-count">{s.count} messages</span>
                                            </div>
                                            <span className="item-amount">₹{s.total.toFixed(2)}</span>
                                        </div>
                                    ))}
                                </div>
                            </div>

                            <div className="report-card">
                                <h3>Top Performing Campaigns</h3>
                                <div className="campaign-list">
                                    {data.topCampaigns.map((c, i) => (
                                        <div key={i} className="campaign-item">
                                            <div className="item-main">
                                                <span className="name">{c.name}</span>
                                                <span className="sent">{c.sent.toLocaleString()} sent</span>
                                            </div>
                                            <div className="item-stats">
                                                <span className="read">{((c.read / c.delivered) * 100 || 0).toFixed(1)}% read</span>
                                                <span className={`status-badge ${c.status.toLowerCase()}`}>{c.status}</span>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </DashboardLayout>
    );
};

export default Reports;
